/**
 * SN-native clinic staffing QB section
 * (`GET /clinics/{clinicSn}/staffing`). These interfaces mirror the clinics-api
 * `StaffingSection` payload 1:1 (snake_case field names, all optional) so the
 * JSON response maps with no transform.
 *
 * The nested VK atoms (`AysStaffingPersonalerfassung`,
 * `AysStaffingBerufsgruppe`, `AysStaffingPersonnelVk`,
 * `AysStaffingPersonnelHeadcount`) intentionally share the shape of the
 * per-department staffing types in `department.model.ts`, so the same shared
 * presentational unit (`libs/clinic-lib/src/lib/staffing`) renders both grains.
 *
 * NOTE: unrelated to the legacy year-keyed `AysClinicStaffing` in
 * `staffing.model.ts` (still used by the clinics-filter result model) — these
 * are the new report-year QB section types.
 */

/** `StaffHeadcount` — a profession headcount in full-time equivalents. */
export interface AysStaffHeadcount {
  beruf?: string;
  anzahl_vk?: number;
}

/** `PersonnelVk` — a full-time-equivalent count with an explanation. */
export interface AysStaffingPersonnelVk {
  anzahl_vk?: number;
  erlaeuterungen?: string;
}

/** `PersonnelHeadcount` — a head count with an explanation and a ratio. */
export interface AysStaffingPersonnelHeadcount {
  anzahl?: number;
  erlaeuterungen?: string;
  fall_je_anzahl?: string;
}

/**
 * `StationaereVersorgung` — the stationäre share of a Personalerfassung: a VK
 * count plus the Fälle-je-Vollkraft ratio (a de-DE decimal *string*, e.g.
 * "307,81427"). Only the department grain currently fills the ratio; on this
 * clinic-grain response it is present but null (BDA-5841).
 */
export interface AysStaffingStationaereVersorgung {
  anzahl_vk?: number;
  erlaeuterungen?: string;
  fall_je_anzahl?: string;
}

/** `Versorgungsform` — VK split by ambulant / stationär care. */
export interface AysStaffingVersorgungsform {
  ambulante_versorgung?: AysStaffingPersonnelVk;
  stationaere_versorgung?: AysStaffingStationaereVersorgung;
}

/** `Beschaeftigungsverhaeltnis` — VK split by direct / indirect employment. */
export interface AysStaffingBeschaeftigungsverhaeltnis {
  personal_mit_direktem_bv?: AysStaffingPersonnelVk;
  personal_ohne_direktem_bv?: AysStaffingPersonnelVk;
}

/** `Personalerfassung` — the detailed VK breakdown for a Berufsgruppe. */
export interface AysStaffingPersonalerfassung {
  anzahl_vk?: number;
  erlaeuterungen?: string;
  versorgungsform?: AysStaffingVersorgungsform;
  beschaeftigungsverhaeltnis?: AysStaffingBeschaeftigungsverhaeltnis;
}

/** `StaffingBerufsgruppe` — a profession group with (optionally) two captures. */
export interface AysStaffingBerufsgruppe {
  beruf?: string;
  personalerfassung?: AysStaffingPersonalerfassung;
  personalerfassung_ohne_fachabteilungszuordnung?: AysStaffingPersonalerfassung;
}

/** `Fachaerzte` — the specialist-doctor capture within an Ärzte block. */
export interface AysStaffingFachaerzte {
  personalerfassung?: AysStaffingPersonalerfassung;
}

/** `StaffingAerzteBlock` — a doctor capture (with or without Belegärzte). */
export interface AysStaffingAerzteBlock {
  massgebliche_tarifliche_wochenarbeitszeit?: string;
  personalerfassung?: AysStaffingPersonalerfassung;
  fachaerzte?: AysStaffingFachaerzte;
}

/** `StaffingAerzte` — the ärztliches-Personal breakdown. */
export interface AysStaffingAerzte {
  keine_aerzte_ohne_belegaerzte?: boolean;
  belegaerzte?: AysStaffingPersonnelHeadcount;
  aerzte_ohne_belegaerzte?: AysStaffingAerzteBlock;
  aerzte_ohne_fachabteilungszuordnung?: AysStaffingAerzteBlock;
}

/** `Therapeuten` — psychiatric therapist profession groups. */
export interface AysStaffingTherapeuten {
  berufsgruppen?: AysStaffingBerufsgruppe[];
}

/** `AusgewaehltesPersonalPsych` — selected psychiatric personnel. */
export interface AysStaffingPersonalPsych {
  therapeuten?: AysStaffingTherapeuten;
  genesungsbegleitung?: AysStaffingPersonnelVk;
}

/** `Pflegekraefte` — nursing staff detail. */
export interface AysStaffingPflegekraefte {
  berufsgruppen?: AysStaffingBerufsgruppe[];
  massgebliche_tarifliche_wochenarbeitszeit?: string;
  beleghebammen_belegentbindungspfleger?: AysStaffingPersonnelHeadcount;
}

/** `StaffingDetailSection` — the detailed medical / nursing / psych breakdown. */
export interface AysStaffingDetail {
  aerzte?: AysStaffingAerzte;
  ausgewaehltes_personal_psych?: AysStaffingPersonalPsych;
  pflegekraefte?: AysStaffingPflegekraefte;
}

/**
 * `SpecialTherapeuticPersonnelEntry` — one spezialtherapeutisches Personal row,
 * keyed by an `SP`-catalog code (`sp_key`, e.g. "SP26"); `legacy_*` carry the
 * older headcount shape for reporting years before the Personalerfassung split.
 */
export interface AysSpecialTherapeuticPersonnelEntry {
  sp_key: string;
  personalerfassung?: AysStaffingPersonalerfassung;
  legacy_anzahl?: number;
  legacy_erlaeuterungen?: string;
}

/** `SpecialTherapeuticPersonnelSection` — the spezialtherapeutisches Personal list. */
export interface AysSpecialTherapeuticPersonnelSection {
  entries: AysSpecialTherapeuticPersonnelEntry[];
}

/**
 * `NursingLowerLimitEntry` — a Pflegepersonaluntergrenzen (PpUGV) row: the
 * degree to which the mandated nurse-to-patient ratio was met, per shift,
 * station and pflegesensitivem Bereich.
 */
export interface AysNursingLowerLimitEntry {
  schicht?: string;
  station?: string;
  pflegesensitiver_bereich?: string;
  erfuellungsgrad?: number;
  ausnahmetatbestaende?: number;
}

/** `NursingLowerLimitsSection` — the month- and shift-based PpUGV tables. */
export interface AysNursingLowerLimitsSection {
  monatsbezogen: AysNursingLowerLimitEntry[];
  schichtbezogen: AysNursingLowerLimitEntry[];
}

/** `StaffingSection` — the whole SN-native clinic staffing QB section. */
export interface AysClinicStaffingSection {
  personnel?: AysStaffHeadcount[];
  staffingDetail?: AysStaffingDetail;
  specialTherapeuticPersonnel?: AysSpecialTherapeuticPersonnelSection;
  nursingLowerLimits?: AysNursingLowerLimitsSection;
}
